// PWA 설치 배너 — beforeinstallprompt 감지 → 홈 화면 추가 유도
// 닫기 시 7일간 숨김 (localStorage)
import { useEffect, useState } from 'react'
import { Download, X } from 'lucide-react'

const DISMISS_KEY = 'canvly-pwa-dismissed-at'
const DISMISS_DAYS = 7

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export default function PWAInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    // 이미 설치돼 standalone 모드로 실행 중이면 배너 숨김
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true
    if (isStandalone) return

    // 최근 7일 내 닫았으면 숨김
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) ?? 0)
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_DAYS * 24 * 60 * 60 * 1000) return

    const handler = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      setVisible(true)
    }

    window.addEventListener('beforeinstallprompt', handler)

    // 설치 완료 시 배너 숨김
    const installedHandler = () => {
      setVisible(false)
      setDeferredPrompt(null)
    }
    window.addEventListener('appinstalled', installedHandler)

    return () => {
      window.removeEventListener('beforeinstallprompt', handler)
      window.removeEventListener('appinstalled', installedHandler)
    }
  }, [])

  const handleInstall = async () => {
    if (!deferredPrompt) return
    await deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    if (outcome === 'accepted') {
      setVisible(false)
      setDeferredPrompt(null)
    }
  }

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()))
    setVisible(false)
  }

  if (!visible || !deferredPrompt) return null

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 md:bottom-4 md:left-auto md:right-4 md:max-w-sm">
      <div className="bg-canvly-charcoal text-canvly-warm border-t border-canvly-gold/40 md:border md:border-canvly-gold/30 md:rounded-sm shadow-lg">
        <div className="flex items-start gap-3 p-4">
          <div className="flex-shrink-0 w-10 h-10 bg-canvly-gold/10 border border-canvly-gold/30 rounded-sm flex items-center justify-center">
            <Download className="w-4 h-4 text-canvly-gold" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-serif text-sm text-canvly-warm mb-0.5">홈 화면에 Canvly 추가</p>
            <p className="text-[11px] text-canvly-mist leading-relaxed">
              바로가기를 추가하면 앱처럼 빠르게 실행할 수 있습니다.
            </p>
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleInstall}
                className="px-3 py-1.5 bg-canvly-gold text-canvly-charcoal text-xs font-medium rounded-sm hover:bg-canvly-gold-lt transition-colors"
              >
                설치하기
              </button>
              <button
                onClick={handleDismiss}
                className="px-3 py-1.5 border border-white/20 text-canvly-mist text-xs rounded-sm hover:bg-white/5 transition-colors"
              >
                나중에
              </button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            aria-label="닫기"
            className="flex-shrink-0 text-canvly-mist hover:text-canvly-warm transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
